import { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, Tune, Search } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import {
  CTABanner,
  ExploreFilters,
  PartnerCard,
  PartnerCardSkeleton,
  PartnershipInquiryModal,
} from "components/partners";
import Layout from "components/Layout";
import { useGetPartnersQuery } from "features/partners/partnersApi";
import type { Partner, PartnerFilters } from "types/partners";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";

const PAGE_SIZE = 100;
const COLLAPSE_AT = 60;
const RESTORE_AT = 15;

export default function PartnerDirectoryPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(900));
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const [filters, setFilters] = useState<PartnerFilters>({
    search: "",
    partner_type: "",
    industry_type: "",
    sort: "name",
  });

  const [page, setPage] = useState(1);
  const [accumulatedPartners, setAccumulatedPartners] = useState<Partner[]>([]);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasFiltersEverApplied, setHasFiltersEverApplied] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(
    null,
  );

  /**
   * Active filters state
   * Used to:
   * - hide featured section
   * - remove extra top padding
   */
  const hasActiveFilters = !!(
    filters.search ||
    filters.partner_type ||
    filters.industry_type ||
    filters.featured
  );

  /**
   * Check if URL has search params
   */
  const hasSearchParams = Array.from(searchParams).length > 0;

  /**
   * Only show featured section after filters are initialized from URL
   * and only if there are no active filters AND filters have never been applied
   * If URL has no search params, show immediately without waiting
   */
  const shouldShowFeatured =
    (hasSearchParams ? filtersInitialized : true) &&
    !hasActiveFilters &&
    !hasFiltersEverApplied;

  /**
   * Initialize filters from URL
   */
  useEffect(() => {
    const search = searchParams.get("search") || "";
    const partner_type = (searchParams.get("partner_type") || "") as any;
    const industry_type = (searchParams.get("industry_type") || "") as any;
    const sort = (searchParams.get("sort") || "name") as any;
    const featured = searchParams.get("featured") === "true";

    setFilters({
      search,
      partner_type,
      industry_type,
      sort,
      featured,
    });
    setFiltersInitialized(true);

    // If URL has any filter params, mark filters as ever applied
    if (search || partner_type || industry_type || featured) {
      setHasFiltersEverApplied(true);
    }
  }, [searchParams]);

  const {
    data: partnersData,
    isLoading,
    isFetching,
    isError,
  } = useGetPartnersQuery({
    ...filters,
    page,
    page_size: PAGE_SIZE,
  });

  const { data: featuredData, isLoading: isFeaturedLoading } =
    useGetPartnersQuery({
      featured: true,
    });

  const { data: allPartnersData } = useGetPartnersQuery({
    page_size: 1000,
  });

  const totalCount = partnersData?.count || 0;
  const featuredPartners = featuredData?.results || [];
  const allPartners = allPartnersData?.results || [];

  const hasMore = accumulatedPartners.length < totalCount && !hasReachedEnd;

  /**
   * Accumulate partners across pages
   */
  useEffect(() => {
    if (partnersData?.results) {
      if (page === 1) {
        setAccumulatedPartners(partnersData.results);
        setHasReachedEnd(false);
        setIsLoadingMore(false);
      } else {
        setAccumulatedPartners((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));

          const newPartners = partnersData.results.filter(
            (p) => !existingIds.has(p.id),
          );

          return [...prev, ...newPartners];
        });
        setIsLoadingMore(false);
      }

      if (partnersData.results.length < PAGE_SIZE) {
        setHasReachedEnd(true);
      }
    } else if (!isLoading && page > 1) {
      setHasReachedEnd(true);
      setIsLoadingMore(false);
    }

    if (isError && page > 1) {
      setHasReachedEnd(true);
      setIsLoadingMore(false);
    }
  }, [partnersData, page, isLoading, isError]);

  /**
   * Find actual scroll container
   */
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const interval = setInterval(() => {
      if (!anchorRef.current) {
        attempts++;

        if (attempts >= maxAttempts) {
          clearInterval(interval);
        }

        return;
      }

      let el: HTMLElement | null = anchorRef.current.parentElement;

      while (el) {
        const style = window.getComputedStyle(el);

        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          el.style.overflowAnchor = "none";

          setScrollContainer(el);

          clearInterval(interval);

          return;
        }

        el = el.parentElement;
      }

      attempts++;

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setScrollContainer(document.documentElement);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  /**
   * Collapse on scroll
   */
  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (isMobile) return;

      const y = scrollContainer.scrollTop;

      if (y > COLLAPSE_AT) {
        setCollapsed(true);
      } else if (y < RESTORE_AT && !hasActiveFilters) {
        setCollapsed(false);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainer, !shouldShowFeatured, isMobile]);

  /**
   * Filters changed
   */
  const handleFiltersChange = (newFilters: PartnerFilters) => {
    setFilters(newFilters);

    // Mark that filters have been applied if any filter is active
    if (
      newFilters.search ||
      newFilters.partner_type ||
      newFilters.industry_type ||
      newFilters.featured
    ) {
      setHasFiltersEverApplied(true);
    }

    setPage(1);
    setAccumulatedPartners([]);
    setHasReachedEnd(false);

    const params = new URLSearchParams();

    if (newFilters.search) {
      params.set("search", newFilters.search);
    }

    if (newFilters.partner_type) {
      params.set("partner_type", newFilters.partner_type);
    }

    if (newFilters.industry_type) {
      params.set("industry_type", newFilters.industry_type);
    }

    if (newFilters.sort) {
      params.set("sort", newFilters.sort);
    }

    if (newFilters.featured) {
      params.set("featured", "true");
    }

    setSearchParams(params);
  };

  /**
   * Clear filters
   */
  const handleClearFilters = () => {
    const clearedFilters: PartnerFilters = {
      search: "",
      partner_type: "",
      industry_type: "",
      sort: filters.sort,
      featured: undefined,
    };

    setFilters(clearedFilters);

    setPage(1);
    setAccumulatedPartners([]);
    setHasReachedEnd(false);

    const params = new URLSearchParams();

    if (clearedFilters.sort) {
      params.set("sort", clearedFilters.sort);
    }

    setSearchParams(params);
  };

  /**
   * Infinite scroll
   */
  const handleScroll = useCallback(() => {
    if (!scrollContainer || isLoadingMore) return;

    const scrollTop = scrollContainer.scrollTop;
    const clientHeight = scrollContainer.clientHeight;
    const scrollHeight = scrollContainer.scrollHeight;

    const threshold = 300;

    if (
      scrollTop + clientHeight >= scrollHeight - threshold &&
      !isFetching &&
      hasMore
    ) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  }, [scrollContainer, isFetching, hasMore, isLoadingMore]);

  useEffect(() => {
    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [handleScroll, scrollContainer]);

  return (
    <Layout title="Partner Directory">
      <Box
        sx={{
          backgroundColor: "#F3F4F5",
          minHeight: "100%",
        }}
      >
        {/* Scroll anchor */}
        <Box
          ref={anchorRef}
          sx={{
            height: 0,
            overflow: "hidden",
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            pb: collapsed || !shouldShowFeatured ? 0 : 4,
            transition: "padding-bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* PAGE TITLE */}
          <Box
            sx={{
              pt: collapsed && shouldShowFeatured ? "152px" : "32px",

              pb: collapsed || !shouldShowFeatured ? 0 : "32px",

              transition:
                "padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1), padding-bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                fontSize: "2.25rem",
                fontWeight: 700,
                color: "#1F2933",
                mb: 0.5,
              }}
            >
              Our Partner Directory
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                fontSize: "0.875rem",
                color: "#3C4046",
              }}
            >
              Browse partners by industry or integration type, or choose a
              partner to learn more and connect with our team.
            </Typography>
          </Box>

          {/* FEATURED */}
          <Box
            sx={{
              display: "grid",

              gridTemplateRows:
                collapsed || !shouldShowFeatured ? "0fr" : "1fr",

              transition:
                "grid-template-rows 0.5s cubic-bezier(0.4, 0, 0.2, 1)",

              overflowAnchor: "none",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",

                opacity: collapsed || !shouldShowFeatured ? 0 : 1,

                transform:
                  collapsed || !shouldShowFeatured
                    ? "translateY(-10px)"
                    : "translateY(0)",

                transition: "opacity 0.4s ease, transform 0.4s ease",
              }}
            >
              <Box sx={{ pb: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 400,
                    mb: 0.5,
                    color: "text.primary",
                  }}
                >
                  Featured Partners
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    color: "text.secondary",
                    mb: 3,
                  }}
                >
                  Partners recently added to the CalibrateHCM ecosystem.
                </Typography>

                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  sx={{ mb: 4, px: 2 }}
                >
                  {isFeaturedLoading
                    ? Array.from({ length: 5 }).map((_, index) => (
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={4}
                          lg={3}
                          xl={2.4}
                          key={`featured-skeleton-${index}`}
                        >
                          <PartnerCardSkeleton />
                        </Grid>
                      ))
                    : featuredPartners.slice(0, 5).map((partner) => (
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={4}
                          lg={3}
                          xl={2.4}
                          key={partner.id}
                        >
                          <PartnerCard partner={partner} featured />
                        </Grid>
                      ))}
                </Grid>
              </Box>
            </Box>
          </Box>
        </Container>

        {/* TOP CTA */}
        <Box
          sx={{
            display: "grid",

            gridTemplateRows: collapsed || !shouldShowFeatured ? "0fr" : "1fr",

            transition: "grid-template-rows 0.5s cubic-bezier(0.4, 0, 0.2, 1)",

            overflowAnchor: "none",
            mb: 4,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",

              opacity: collapsed || !shouldShowFeatured ? 0 : 1,

              transition: "opacity 0.4s ease",
            }}
          >
            <Container maxWidth="xl">
              <CTABanner onContactClick={() => setIsInquiryModalOpen(true)} />
            </Container>
          </Box>
        </Box>

        <Container maxWidth="xl">
          {/* EXPLORE HEADER */}
          <Box
            sx={{
              display: "grid",

              gridTemplateRows:
                collapsed || !shouldShowFeatured ? "0fr" : "1fr",

              transition:
                "grid-template-rows 0.5s cubic-bezier(0.4, 0, 0.2, 1)",

              overflowAnchor: "none",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",

                opacity: collapsed || !shouldShowFeatured ? 0 : 1,

                transition: "opacity 0.4s ease",
              }}
            >
              <Box sx={{ mt: 5, mb: 0.5 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 400,
                    mb: 0.5,
                    color: "text.primary",
                  }}
                >
                  Explore All Partners
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    mb: 0.5,
                    color: "text.secondary",
                  }}
                >
                  Explore all of the partners across industries that
                  CalibrateHCM works with.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>

        {/* FILTERS */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#F3F4F5",
            py: 1,
          }}
        >
          <Container maxWidth="xl">
            {isSmallScreen ? (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
              >
                <TextField
                  placeholder="Search partners"
                  value={filters.search}
                  onChange={(e) =>
                    handleFiltersChange({ ...filters, search: e.target.value })
                  }
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    "& .MuiInputBase-root": { height: "40px" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  color="primary"
                  aria-label="Open filters"
                  onClick={() => setFiltersDrawerOpen(true)}
                  sx={{
                    flexShrink: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Tune />
                </IconButton>
              </Box>
            ) : (
              <ExploreFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
              />
            )}
          </Container>
        </Box>

        <Container maxWidth="xl">
          {/* PARTNERS GRID */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isLoading || (isFetching && accumulatedPartners.length === 0) ? (
              Array.from({ length: 12 }).map((_, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2.4}
                  key={`skeleton-${index}`}
                >
                  <PartnerCardSkeleton />
                </Grid>
              ))
            ) : accumulatedPartners.length === 0 ? (
              <Grid item xs={12}>
                <Typography
                  textAlign="center"
                  color="text.secondary"
                  sx={{ py: 6 }}
                >
                  No partners found matching your criteria.
                </Typography>
              </Grid>
            ) : (
              accumulatedPartners.map((partner) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2.4}
                  key={partner.id}
                >
                  <PartnerCard partner={partner} />
                </Grid>
              ))
            )}
          </Grid>

          {/* INFINITE SCROLL */}
          {!isLoading && accumulatedPartners.length > 0 && (
            <Box
              ref={sentinelRef}
              sx={{
                height: 20,
                mt: 2,
              }}
            />
          )}

          {isFetching && accumulatedPartners.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                mb: 2,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Container>

        {/* BOTTOM CTA */}
        <Container maxWidth="xl">
          <Box sx={{ mt: 0, mb: 6 }}>
            <CTABanner onContactClick={() => setIsInquiryModalOpen(true)} />
          </Box>
        </Container>

        <PartnershipInquiryModal
          open={isInquiryModalOpen}
          onClose={() => setIsInquiryModalOpen(false)}
          partners={allPartners}
        />

        {/* FILTERS DRAWER */}
        {isSmallScreen && (
          <Drawer
            anchor="bottom"
            open={filtersDrawerOpen}
            onClose={() => setFiltersDrawerOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
                maxHeight: "85vh",
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  Filters
                </Typography>
                <IconButton
                  onClick={() => setFiltersDrawerOpen(false)}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Box>
              <ExploreFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                columnLayout
              />
            </Box>
          </Drawer>
        )}
      </Box>
    </Layout>
  );
}
